import React, { useEffect, useMemo, useState } from 'react'
import styles from 'styles/components/ChangePlanInvoiceForm.module.sass'
import { Box, Step, StepLabel, Stepper } from '@mui/material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import zeroneLogoDark from 'assets/images/svg/logo-dark.svg'
import ZeroneLoader from 'components/common/ZeroneLoader';
import AccountType from 'interface/AccountType';
import { useToast } from 'provider/ToastProvider';
import ProrationChargeCard from 'components/cards/ProrationChargeCard';
import SubscriptionInvoiceConfirmationCard from 'components/cards/SubscriptionInvoiceConfirmationCard';
import { usePlanStatus } from 'provider/PlanStatusProvider';

interface ChangePlanInvoiceFormProps {
    accountType: 'free' | 'basic' | 'sapphire' | 'gold' | 'platinum';
    billingCycle: 'monthly' | 'annual';
}

const steps = ['Select Plan', 'Invoice Details', 'Confirmation'];

/**
 * ChangePlanInvoiceForm component - Form to change subscription plan with invoice preview and confirmation.
 * 
 * This component allows users to change their subscription plan, preview the invoice for the new plan,
 * and confirm the changes before applying them.
 *
 * @component ChangePlanInvoiceForm
 * 
 * @returns {JSX.Element} ChangePlanInvoiceForm component 
 * @example
 * // Example usage:
 * <ChangePlanInvoiceForm />
 */
const ChangePlanInvoiceForm: React.FC = () => {
    const [loading, setLoading] = React.useState<boolean>(false);
    const [selectedPlanName, setSelectedPlanName] = useState<ChangePlanInvoiceFormProps['accountType'] | null>(null);
    const [selectedBillingCycle, setSelectedBillingCycle] = useState<ChangePlanInvoiceFormProps['billingCycle']>('monthly');
    const [showInvoicePreview, setShowInvoicePreview] = useState<boolean>(false);
    const [showInvoiceConfirmationCard, setShowInvoiceConfirmationCard] = useState<boolean>(false);
    const [beginChangeProcess, setBeginChangeProcess] = useState<boolean>(false);
    const [nextInvoiceRecurringCents, setNextInvoiceRecurringCents] = useState<number | null>(null);
    const [currentStep, setCurrentStep] = useState<number>(1);
    
    const accountTypes = usePlanStatus().activeAccountTypes;

    const location = useLocation();
    const navigate = useNavigate();
    const { addToast } = useToast(); 

    const routeState = (location.state || {}) as ChangePlanInvoiceFormProps;

    useEffect(() => {
        if (!routeState || !routeState.accountType || !routeState.billingCycle) {
            addToast({
                type: 'error',
                message: 'Missing account type or billing cycle. Please select a plan first.'
            });
            navigate('/change-plan');
            return;
        }
        
        setSelectedPlanName(routeState.accountType);
        setSelectedBillingCycle(routeState.billingCycle);
        setShowInvoicePreview(true);
        setCurrentStep(1);
    }, [routeState]);

    const selectedAccountType: AccountType | null = useMemo(() => {
        if (!selectedPlanName) return null;
        return accountTypes.find(at => at.name === selectedPlanName) || null;
    }, [selectedPlanName]);

    const handleChangePlan = () => {
        setLoading(true);
        navigate('/change-plan');
    }

    const onConfirmClicked = () => {
        setLoading(false)
        setCurrentStep(3)
        setShowInvoicePreview(false)
        setShowInvoiceConfirmationCard(true)
        setBeginChangeProcess(true)
    }

    const onConfirmationFailed = () => {
        setLoading(false);
        setCurrentStep(2);
        setBeginChangeProcess(false);
        setShowInvoiceConfirmationCard(false);
        setShowInvoicePreview(true);
    }

    const onConfirmationSuccess = () => {
        setLoading(false);
        setShowInvoiceConfirmationCard(false);
        setShowInvoicePreview(false);

        /**
         * Trigger auth change event to update user subscription.
         * This will ensure that the user's subscription status is updated across the app.
         * 
         * Navigate to home page after successful subscription change.
         * TODO: Refactor this to use a more robust state management solution in the future.
         */
        window.dispatchEvent(new Event('authChange'));
        navigate('/');
    }

    return (
        <div className={styles['change-plan-invoice-form']}>
            <div className={styles['header-container']}>  
                <div className={styles['logo-container']}>
                    <Link to="/">
                        <img src={zeroneLogoDark} className={styles['zerone-logo']} alt="Zerone AnalytiQs Logo" />
                    </Link>
                </div>
                <h2 className={styles['form-title']}>{!loading ? 'Confirm Plan Change' : ''}</h2>
                {/* <p className={styles['form-subtitle']}>{!loading ? `We will automatically bill each ${selectedBillingCycle == 'monthly' ? 'month' : 'year'}.` : ''}</p> */}
            </div>
            <div className={styles['stepper-container']}>
                <Box sx={{ width: '100%' }}>
                    <Stepper activeStep={currentStep} alternativeLabel>
                        {steps.map((label, index) => (
                            <Step key={index}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </Box>
            </div>
            <div className={styles['main-container']}>
                <div className={styles['summary-section']}>
                    <div className={styles['top-summary']}>
                        <label className={styles['radio-label']}>
                            <input 
                                type="radio" 
                                name="accountType"
                                value={selectedBillingCycle}
                                checked={true}
                                className={styles['radio-input']}
                                readOnly
                            />
                            <span className={styles['radio-text']} style={{ color: selectedAccountType?.color }}>{selectedAccountType?.name}</span>
                        </label>
                        {nextInvoiceRecurringCents !== null ? (
                            <div className={styles['right']}>
                                <p className={styles['amount']}>{'USD'} {isNaN(nextInvoiceRecurringCents) ? '0.00': (nextInvoiceRecurringCents / 100).toFixed(2) || '0.00'}</p>
                                <span className={styles['subscription-type']}>{selectedBillingCycle === 'monthly' ? 'per month' : 'per year'}</span>
                            </div>
                        ) : null}
                    </div>
                    <p className={styles['account-description']}>{selectedAccountType?.description}</p>
                    <span className={styles['change-plan']} onClick={handleChangePlan}>Change plan</span>
                </div>
                <div className={styles['divider']} />
                <div className={styles['payment-container']}>
                    { showInvoicePreview && selectedAccountType ? (
                        <ProrationChargeCard
                            selectedAccount={selectedAccountType}
                            billingCycle={selectedBillingCycle}
                            setnextInvoiceRecurringCents={setNextInvoiceRecurringCents}
                            setShowInvoicePreview={setShowInvoicePreview}
                            setLoading={setLoading}
                            setCurrentStep={setCurrentStep}
                            onConfirm={onConfirmClicked}
                        />
                    ) : showInvoiceConfirmationCard && selectedAccountType && beginChangeProcess ? (
                        <SubscriptionInvoiceConfirmationCard
                            selectedAccount={selectedAccountType}
                            billingCycle={selectedBillingCycle}
                            beginChangeProcess={beginChangeProcess}
                            setLoading={setLoading}
                            onConfirmationSuccess={onConfirmationSuccess}
                            onConfirmationFailed={onConfirmationFailed}
                        />
                    ) : null }
                    {/* An overlay, not a sibling in normal flow: ZeroneLoader centers itself via
                        top/left/transform, which only behaves under position:absolute. Rendered as a
                        plain sibling here it visually collided with the card/button above it instead of
                        replacing them. This also swaps the message once we're past the preview step -
                        "Loading invoice preview..." was showing verbatim while a card was being
                        confirmed/activated, well after any preview had loaded. */}
                    {loading && (
                        <div className={styles['loading-overlay']}>
                            <ZeroneLoader text={showInvoiceConfirmationCard ? 'Processing your payment...' : 'Loading invoice preview...'} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
export default ChangePlanInvoiceForm